/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/**
 * @author Irrevaldy
 * B2B-5564 — Product Stock (Inventory Stock)
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import Clipboard from '@react-native-clipboard/clipboard';
import Toast from 'react-native-toast-message';
import { LegendList } from '@legendapp/list';

import * as Layout from '@components/screen/Layout';
import SelectBox from '@components/form/SelectBox';
import SociollaText from '@components/sociolla/SociollaText';
import RenderIf from '@components/global/RenderIf';
import ModalHalf from '@components/modal/ModalHalf';
import Table from '@components/table/Table';
import Image from '@components/global/Image';

import { isWeb, isAndroid } from '@global/screens';
import Colors from '@global/colors';
import { parseDate } from '@global/helpers';
import {
  getProductStockSalesTeams,
  getProductStockSearch,
  getProductStockDetail,
} from '@api/ms-order';
import useApi from '@/hooks/useApi';
import Pagination from '@components/table/Pagination';
import { translate } from '@global/translate';

const TABLE_STRUCTURE = {
  brand: { head: 'Brand', body: 'brand' },
  name: { head: 'Name', body: 'name' },
  varian: { head: 'Varian', body: 'varian' },
  reference: { head: 'Reference', body: 'reference', type: 'slot' },
  ean: { head: 'EAN', body: 'ean' },
  sales_team: { head: 'Sales Team', body: 'sales_team' },
  sellable_qty: { head: 'Sellable Stock', body: 'sellable_qty', type: 'slot' },
  expired_qty: { head: 'Clearance Stock', body: 'expired_qty', type: 'slot' },
};

const DETAIL_MODAL_NONE = null;
const DETAIL_MODAL_SELLABLE = 'sellable';
const DETAIL_MODAL_EXPIRED = 'expired';

const formatNumber = (num) => {
  if (num == null) {
    return '0';
  }
  return Number(num).toLocaleString();
};

const ProductStockScreen = () => {
  const user = useSelector((state) => state.auth.user);
  const didFetchRef = useRef(false);
  const isLoadingMoreRef = useRef(false);

  const [lastUpdated, setLastUpdated] = useState(null);
  const [salesTeams, setSalesTeams] = useState([]);
  const [selectedSalesTeam, setSelectedSalesTeam] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(isWeb ? 10 : 15);

  const [isSearching, setIsSearching] = useState(false);
  const [detailModal, setDetailModal] = useState(DETAIL_MODAL_NONE);
  const [detailData, setDetailData] = useState(null);

  const salesTeamApi = useApi(getProductStockSalesTeams);
  const searchApi = useApi(getProductStockSearch);
  const detailApi = useApi(getProductStockDetail);

  const hasOneSalesTeam = user?.sales_team?.length === 1;
  const hasMultipleSalesTeams = (user?.sales_team?.length || 0) > 1;

  useFocusEffect(
    useCallback(() => {
      if (!didFetchRef.current) {
        didFetchRef.current = true;
        loadSalesTeams();
      }
    }, []),
  );

  const loadSalesTeams = async () => {
    try {
      if (hasMultipleSalesTeams) {
        // Populate dropdown from the user's own assigned teams
        setSalesTeams(
          user.sales_team.map((t) => ({
            label: t.name || t.label,
            value: t.id || t._id,
          })),
        );
        setLastUpdated(new Date());
        return;
      }

      const res = await salesTeamApi.request({ params: {} });
      const teams = res?.data?.data || [];
      setSalesTeams(
        teams.map((t) => ({
          label: t.sales_team_name,
          value: t.sales_team_id,
          warehouses: t.warehouses,
        })),
      );

      const apiDate = res?.data?.last_updated || res?.data?.meta?.last_updated;
      setLastUpdated(apiDate ? new Date(apiDate) : new Date());

      if (hasOneSalesTeam) {
        const userTeam = user.sales_team[0];
        setSelectedSalesTeam({
          label: userTeam.name || userTeam.label,
          value: userTeam.id || userTeam._id,
        });
      }
    } catch (_) {
      setLastUpdated(new Date());
    }
  };

  const doSearch = async (
    newSkip = 0,
    append = false,
    limitOverride = null,
  ) => {
    if (searchKeyword.length < 3) {
      Toast.show({
        text1: 'Please enter at least 3 characters.',
        type: 'error',
      });
      return;
    }
    if (!selectedSalesTeam) {
      Toast.show({ text1: 'Please select a sales team first.', type: 'error' });
      return;
    }

    const activeLimit = limitOverride || limit;
    const page = Math.floor(newSkip / activeLimit) + 1;
    const filter = {
      search_keyword: searchKeyword,
      page,
      limit: activeLimit,
    };

    if (selectedSalesTeam) {
      filter.sales_team_id = selectedSalesTeam.value;
    }

    if (!append) {
      setResults([]);
    }
    setIsSearching(true);
    try {
      const res = await searchApi.request({ params: { filter } });
      const body = res?.data;
      const newData = body?.data || [];
      setResults((prev) => (append ? [...prev, ...newData] : newData));
      setTotalCount(body?.meta?.total || 0);
      setSkip(newSkip);
      setHasSearched(true);
      if (body?.meta?.last_updated) {
        setLastUpdated(new Date(body.meta.last_updated));
      }
    } catch (_) {
      if (!append) {
        setResults([]);
        setTotalCount(0);
      }
      setHasSearched(true);
    } finally {
      setIsSearching(false);
    }
  };

  const loadMore = () => {
    if (
      !isLoadingMoreRef.current &&
      !isSearching &&
      results.length < totalCount
    ) {
      isLoadingMoreRef.current = true;
      doSearch(skip + limit, true).finally(() => {
        isLoadingMoreRef.current = false;
      });
    }
  };

  const onSetLimit = (newLimit) => {
    setLimit(newLimit);
    setSkip(0);
    doSearch(0, false, newLimit);
  };

  const openDetail = async (item, type) => {
    setDetailModal(type);
    setDetailData(null);

    const filter = selectedSalesTeam
      ? { sales_team_id: selectedSalesTeam.value }
      : {};
    filter.ean = item.ean;

    try {
      const res = await detailApi.request(item.sku_id, { params: { filter } });
      setDetailData(res?.data?.data || null);
    } catch (_) {}
  };

  const closeDetail = () => {
    setDetailModal(DETAIL_MODAL_NONE);
    setDetailData(null);
  };

  const slotComponent = {
    reference: ({ data }) => (
      <View style={styles.referenceCell}>
        <SociollaText style={styles.referenceText}>
          {data.reference || '-'}
        </SociollaText>
        <RenderIf condition={!!data.reference}>
          <TouchableOpacity
            style={styles.copyBtn}
            onPress={() => Clipboard.setString(data.reference)}>
            <Image
              source={require('@assets/img/icons/copy.svg')}
              svg={true}
              width={14}
              height={14}
              style={styles.copyIcon}
            />
          </TouchableOpacity>
        </RenderIf>
      </View>
    ),
    sellable_qty: ({ data }) => (
      <View style={styles.qtyCell}>
        <SociollaText style={styles.qtyText}>
          {formatNumber(data.sellable_qty)}
        </SociollaText>
        <TouchableOpacity
          onPress={() => openDetail(data, DETAIL_MODAL_SELLABLE)}
          style={styles.eyeBtn}>
          <Image
            source={require('@assets/img/icons/eye.png')}
            width={16}
            height={16}
            style={styles.eyeIcon}
          />
        </TouchableOpacity>
      </View>
    ),
    expired_qty: ({ data }) => (
      <View style={styles.qtyCell}>
        <SociollaText style={[styles.qtyText, { color: Colors.red1 }]}>
          {formatNumber(data.expired_qty)}
        </SociollaText>
        <TouchableOpacity
          onPress={() => openDetail(data, DETAIL_MODAL_EXPIRED)}
          style={styles.eyeBtn}>
          <Image
            source={require('@assets/img/icons/eye.png')}
            width={16}
            height={16}
            style={styles.eyeIcon}
          />
        </TouchableOpacity>
      </View>
    ),
  };

  const renderDetailRows = (data, type) => {
    if (!data) {
      return null;
    }
    const section =
      type === DETAIL_MODAL_SELLABLE ? data.sellable : data.expired;
    if (!section) {
      return null;
    }

    const rows =
      type === DETAIL_MODAL_SELLABLE
        ? [
            {
              label: 'Stock on hand (pacman)',
              value: section.sellable_stock_pacman,
            },
            { label: 'Reserve stock (CBD)', value: section.cbd_sellable },
            {
              label: 'Reserve stock (DO ready)',
              value: section.do_ready_sellable,
            },
            {
              label: 'Available stock',
              value: section.available_stock_sellable,
              highlight: true,
            },
          ]
        : [
            {
              label: 'Stock on hand (pacman)',
              value: section.expired_stock_pacman,
            },
            { label: 'Reserve stock (CBD)', value: section.cbd_expired },
            {
              label: 'Reserve stock (DO ready)',
              value: section.do_ready_expired,
            },
            {
              label: 'Available stock',
              value: section.available_stock_expired,
              highlight: true,
            },
          ];

    return rows.map((row, i) => (
      <View
        key={i}
        style={[styles.detailRow, row.highlight && styles.detailRowHighlight]}>
        <SociollaText
          style={[
            styles.detailLabel,
            row.highlight && styles.detailLabelHighlight,
          ]}>
          {row.label}
        </SociollaText>
        <SociollaText
          style={[
            styles.detailValue,
            row.highlight && styles.detailValueHighlight,
          ]}>
          {formatNumber(row.value)}
        </SociollaText>
      </View>
    ));
  };

  const renderMobileCard = (item, i) => (
    <View key={i} style={styles.card}>
      <SociollaText style={styles.cardBrand}>{item.brand}</SociollaText>
      <SociollaText style={styles.cardName}>{item.name}</SociollaText>
      <SociollaText style={styles.cardMeta}>{item.varian}</SociollaText>
      <View style={styles.cardRefRow}>
        <SociollaText style={styles.cardRefText}>
          {item.reference || '-'}
        </SociollaText>
        <RenderIf condition={!!item.reference}>
          <TouchableOpacity
            onPress={() => Clipboard.setString(item.reference)}
            style={styles.copyBtn}>
            <Image
              source={require('@assets/img/icons/copy.svg')}
              svg={true}
              width={14}
              height={14}
              style={styles.copyIcon}
            />
          </TouchableOpacity>
        </RenderIf>
      </View>
      <SociollaText style={styles.cardMeta}>{item.sales_team}</SociollaText>
      <View style={styles.cardStockRow}>
        <TouchableOpacity
          style={styles.cardStockBox}
          onPress={() => openDetail(item, DETAIL_MODAL_SELLABLE)}>
          <SociollaText style={styles.cardStockLabel}>
            Sellable Stock
          </SociollaText>
          <SociollaText style={styles.cardStockValue}>
            {formatNumber(item.sellable_qty)}
          </SociollaText>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.cardStockBox}
          onPress={() => openDetail(item, DETAIL_MODAL_EXPIRED)}>
          <SociollaText style={styles.cardStockLabelRed}>
            Clearance Stock
          </SociollaText>
          <SociollaText style={styles.cardStockValueRed}>
            {formatNumber(item.expired_qty)}
          </SociollaText>
        </TouchableOpacity>
      </View>
    </View>
  );

  const SelectTeamBox = (
    <View style={styles.salesTeamWrap}>
      <SelectBox
        placeholder="Select Sales Team"
        label="Select Sales Team"
        hideLabel={true}
        options={salesTeams}
        value={selectedSalesTeam}
        trackBy="value"
        labelKey="label"
        editable={!hasOneSalesTeam}
        isDropdown={true}
        hideSearch={true}
        hideCheck={true}
        hideItemBorder={true}
        onChange={(val) => setSelectedSalesTeam(val)}
      />
    </View>
  );

  const FilterRow = (
    <View style={[styles.filterRow, !isWeb && styles.filterRowMobile]}>
      {SelectTeamBox}

      <RenderIf condition={isWeb}>
        {/* Web: TextInput + search button */}
        <View style={styles.searchContainer}>
          <TextInput
            style={styles.searchInput}
            placeholder={translate('Search')}
            value={searchKeyword}
            onChangeText={setSearchKeyword}
            onSubmitEditing={() => doSearch(0)}
          />
          <TouchableOpacity
            activeOpacity={0.6}
            style={styles.searchBtn}
            onPress={() => doSearch(0)}>
            <SociollaText type="medium" style={styles.searchBtnText}>
              Search
            </SociollaText>
            <Image
              source={require('@assets/img/icons/search-white.svg')}
              svg={true}
              width={12}
              height={12}
              style={{ marginLeft: 8 }}
            />
          </TouchableOpacity>
        </View>
      </RenderIf>

      <RenderIf condition={!isWeb}>
        {/* Mobile: icon-only search input */}
        <View style={styles.searchContainerMobile}>
          <Image
            source={require('@assets/img/icons/search.svg')}
            svg={true}
            width={16}
            height={16}
            style={styles.searchIconMobile}
          />
          <TextInput
            style={styles.searchInputMobile}
            placeholder={translate('Search by')}
            placeholderTextColor={Colors.grey2}
            value={searchKeyword}
            onChangeText={setSearchKeyword}
            onSubmitEditing={() => doSearch(0)}
            returnKeyType="search"
          />
        </View>
      </RenderIf>
    </View>
  );

  return (
    <Layout.Container>
      {/* Web: title + last updated */}
      <RenderIf condition={isWeb}>
        <Layout.Title title="Product Stock">
          <RenderIf condition={!!lastUpdated}>
            <SociollaText style={styles.lastUpdated}>
              Last updated data on :{' '}
              {parseDate(lastUpdated, 'd MMM yyyy HH:mm')}
            </SociollaText>
          </RenderIf>
        </Layout.Title>
      </RenderIf>

      {/* Web: filter row outside white box */}
      <RenderIf condition={isWeb}>{FilterRow}</RenderIf>

      {/* White box — results / empty state */}
      <Layout.Wrapper fullWidth rounded>
        {/* ── Web ─────────────────────────────────────────────────────────── */}
        <RenderIf condition={isWeb}>
          <RenderIf condition={isSearching && !hasSearched}>
            <ActivityIndicator
              size="large"
              color={Colors.blue0}
              style={styles.loader}
            />
          </RenderIf>
          <RenderIf condition={!hasSearched && !isSearching}>
            <View style={styles.emptyState}>
              <Image
                source={require('@assets/img/icons/no-product.svg')}
                svg={true}
                width={80}
                height={80}
                style={styles.emptyIcon}
              />
              <SociollaText style={styles.emptyText}>
                Search for a product, and the results will appear here
              </SociollaText>
            </View>
          </RenderIf>
          <RenderIf condition={hasSearched}>
            <View style={styles.tableWrap}>
              <Table
                name="product-stock"
                structures={TABLE_STRUCTURE}
                data={results}
                slot={slotComponent}
                loading={searchApi.loading}
                totalData={totalCount}
                skip={skip}
                limit={limit}
                onSearch={(newSkip) => doSearch(newSkip)}
                hasNoHide={true}
                showSearch={false}
                showFilter={false}
                showPagination={false}
              />
            </View>
          </RenderIf>
        </RenderIf>

        {/* ── Mobile: LegendList for virtualized, smooth scrolling ────────── */}
        <RenderIf condition={!isWeb}>
          <LegendList
            data={hasSearched ? results : []}
            estimatedItemSize={170}
            keyExtractor={(item, i) => item.ean || i.toString()}
            renderItem={({ item, index }) => renderMobileCard(item, index)}
            recycleItems={!isAndroid}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            style={styles.mobileList}
            contentContainerStyle={styles.mobileScrollContent}
            refreshControl={
              <RefreshControl
                refreshing={false}
                onRefresh={() => {
                  if (hasSearched) {
                    doSearch(0);
                  }
                }}
              />
            }
            ListHeaderComponent={
              <View>
                {FilterRow}
                {!!lastUpdated && (
                  <SociollaText style={styles.lastUpdatedMobile}>
                    Last updated data on :{' '}
                    {parseDate(lastUpdated, 'd MMM yyyy HH:mm')}
                  </SociollaText>
                )}
                {isSearching && !hasSearched && (
                  <ActivityIndicator
                    size="large"
                    color={Colors.blue0}
                    style={styles.loader}
                  />
                )}
                {!hasSearched && !isSearching && (
                  <View style={styles.emptyState}>
                    <Image
                      source={require('@assets/img/icons/no-product.svg')}
                      svg={true}
                      width={80}
                      height={80}
                      style={styles.emptyIcon}
                    />
                    <SociollaText style={styles.emptyText}>
                      Search for a product, and the results will appear here
                    </SociollaText>
                  </View>
                )}
                {hasSearched && isSearching && results.length === 0 && (
                  <ActivityIndicator
                    size="large"
                    color={Colors.blue0}
                    style={styles.loader}
                  />
                )}
                {hasSearched && !isSearching && results.length === 0 && (
                  <View style={styles.emptyState}>
                    <Image
                      source={require('@assets/img/icons/no-product.svg')}
                      svg={true}
                      width={80}
                      height={80}
                      style={styles.emptyIcon}
                    />
                    <SociollaText style={styles.emptyText}>
                      No products found
                    </SociollaText>
                  </View>
                )}
              </View>
            }
            onEndReached={loadMore}
            onEndReachedThreshold={0.5}
            ListFooterComponent={
              isSearching && results.length > 0 ? (
                <ActivityIndicator
                  size="small"
                  color={Colors.blue0}
                  style={styles.loadMoreLoader}
                />
              ) : null
            }
          />
        </RenderIf>
      </Layout.Wrapper>

      {/* Pagination — outside white box */}
      <RenderIf condition={isWeb && hasSearched && !isSearching}>
        <View style={styles.paginationRow}>
          <Pagination
            totalData={totalCount}
            skip={skip}
            limit={limit}
            onSetSkip={(newSkip) => doSearch(newSkip)}
            onSetLimit={(newLimit) => onSetLimit(newLimit)}
            showFirstLast={true}
          />
        </View>
      </RenderIf>

      {/* Detail Modal */}
      <ModalHalf
        visible={detailModal !== DETAIL_MODAL_NONE}
        onClose={closeDetail}
        width={isWeb ? 420 : '100%'}
        maxWidth={isWeb ? 420 : '100%'}
        centered={isWeb}
        title={
          detailModal === DETAIL_MODAL_SELLABLE
            ? 'Detail Sellable Stock'
            : 'Detail Clearance Stock'
        }>
        <View style={styles.detailContainer}>
          <RenderIf condition={detailApi.loading || !detailData}>
            <SociollaText style={styles.loadingText}>Loading...</SociollaText>
          </RenderIf>
          <RenderIf condition={!detailApi.loading && !!detailData}>
            {renderDetailRows(detailData, detailModal)}
          </RenderIf>
        </View>
      </ModalHalf>
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  lastUpdated: {
    fontSize: 13,
    color: Colors.grey2,
  },
  lastUpdatedMobile: {
    fontSize: 13,
    color: Colors.grey2,
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  mobileList: {
    flex: 1,
  },
  mobileScrollContent: {
    flexGrow: 1,
    paddingBottom: 50,
  },
  filterRow: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    paddingBottom: 16,
    gap: 12,
    alignItems: 'center',
    zIndex: isWeb ? 10 : undefined,
  },
  filterRowMobile: {
    flexWrap: 'wrap',
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: 16,
    paddingBottom: 8,
  },
  salesTeamWrap: {
    width: isWeb ? 220 : '100%',
    backgroundColor: Colors.white,
    borderRadius: 5,
    zIndex: isWeb ? 10 : undefined,
  },
  searchContainer: {
    width: isWeb ? 460 : '100%',
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchInput: {
    width: '100%',
    height: 36,
    fontSize: 12,
    fontFamily: 'figtree-regular',
    paddingLeft: 10,
    paddingRight: 100,
    backgroundColor: Colors.white,
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
    borderTopRightRadius: 10,
    borderBottomRightRadius: 10,
    borderWidth: 1,
    borderColor: Colors.grey0,
  },
  searchBtn: {
    height: 36,
    borderTopRightRadius: 10,
    borderBottomRightRadius: 10,
    backgroundColor: Colors.blue3,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
  },
  searchBtnText: {
    fontSize: 14,
    color: Colors.white,
  },
  // Mobile search with icon on left
  searchContainerMobile: {
    width: '100%',
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.grey0,
    paddingHorizontal: 12,
  },
  searchIconMobile: {
    width: 16,
    height: 16,
    marginRight: 8,
    opacity: 0.5,
  },
  searchInputMobile: {
    flex: 1,
    fontSize: 13,
    fontFamily: 'figtree-regular',
    color: Colors.black1,
    padding: 0,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 24,
  },
  emptyIcon: {
    width: 80,
    height: 80,
    marginBottom: 16,
  },
  emptyText: {
    color: Colors.grey2,
    textAlign: 'center',
    fontSize: 14,
  },
  // Reference cell (web table)
  referenceCell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  referenceText: {
    fontSize: 13,
    color: Colors.blue0,
  },
  copyBtn: {
    padding: 4,
  },
  copyIcon: {
    width: 14,
    height: 14,
  },
  // Qty cell (web table)
  qtyCell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  qtyText: {
    fontSize: 14,
  },
  eyeBtn: {
    padding: 4,
  },
  eyeIcon: {
    width: 16,
    height: 16,
  },
  // Mobile card — flat list style with separator, no box/shadow
  card: {
    backgroundColor: Colors.white,
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  cardBrand: {
    fontSize: 11,
    color: Colors.grey2,
    marginBottom: 2,
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  cardName: {
    fontSize: 14,
    color: Colors.black1,
    marginBottom: 2,
  },
  cardMeta: {
    fontSize: 12,
    color: Colors.grey1,
    marginBottom: 2,
  },
  cardRefRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  cardRefText: {
    fontSize: 12,
    color: Colors.blue0,
    marginRight: 4,
  },
  cardStockRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 10,
  },
  cardStockBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: Colors.grey0,
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    alignItems: 'center',
    backgroundColor: Colors.white,
  },
  cardStockLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.black1,
    marginBottom: 4,
  },
  cardStockValue: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.black1,
  },
  cardStockLabelRed: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.red1,
    marginBottom: 4,
  },
  cardStockValueRed: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.red1,
  },
  // Detail modal
  detailContainer: {
    paddingVertical: 8,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey11,
  },
  detailRowHighlight: {
    borderBottomWidth: 0,
    marginTop: 4,
    borderRadius: 6,
  },
  detailLabel: {
    fontSize: 14,
    color: Colors.black3,
    flex: 1,
  },
  detailLabelHighlight: {
    fontWeight: '600',
    color: Colors.black1,
  },
  detailValue: {
    fontSize: 14,
    color: Colors.black1,
    fontWeight: '500',
    flex: 1,
    textAlign: 'right',
  },
  detailValueHighlight: {
    color: Colors.green1,
    fontWeight: '700',
  },
  tableWrap: {
    marginTop: -15,
    marginBottom: -15,
  },
  paginationRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: 12,
  },
  loader: {
    paddingVertical: 40,
  },
  loadMoreLoader: {
    paddingVertical: 20,
  },
  loadingText: {
    textAlign: 'center',
    color: Colors.grey2,
    paddingVertical: 20,
  },
});

export default ProductStockScreen;
